import { useMemo, useRef, useEffect } from 'react'
import { useGLTF } from '@react-three/drei'
import { useKTX2Texture } from './hooks/useModel'
import * as THREE from 'three'
import { MeshBasicNodeMaterial, MeshStandardNodeMaterial } from 'three/webgpu'
import { mood } from '../../lib/world/mood'
import {
  Fn, uniform, attribute, texture, uv,
  vec2, vec3, float,
  fract, sin, cos, floor, abs, max, mix, clamp, mod,
  time, positionLocal
} from 'three/tsl'
import { TREES, TREE_WIND } from '../../lib/world/sceneConfig'

const INITIAL_OFFSET = new THREE.Vector3(99999, 99999, 99999)

// Hardcoded leaf config (previously Leva controls). The leaves are per
// tree: they were 15000 and 150 clusters shared by however many trees
// there were, so the seven added on 30 Oct would have thinned every
// canopy in town by a fifth. These are what each of the 31 had.
const LEAF_CONFIG = {
  leavesPerTree: 15000 / 31,
  clustersPerTree: 150 / 31,
  clusterRadius: 2.4,
  boundsX: 5.5,
  boundsY: 9.0,
  boundsZ: 5.5,
}

// Hardcoded trunk UV config (previously Leva controls)
const TRUNK_UV = {
  scaleU: 2.2,
  scaleV: 2.2,
  offsetU: 0.0,
  offsetV: -0.2,
}

// ---- Seeded random (JS-side) ----
function mulberry32(a) {
  return function () {
    let t = a += 0x6D2B79F5
    t = Math.imul(t ^ t >>> 15, t | 1)
    t ^= t + Math.imul(t ^ t >>> 7, t | 61)
    return ((t ^ t >>> 14) >>> 0) / 4294967296
  }
}

function generateClusteredLeafPositions(treePositions, treeColors, leafCount, clusterCount, clusterRadius, boundingBox, randomSeed) {
  const textureSize = Math.ceil(Math.sqrt(leafCount))
  const totalPixels = textureSize * textureSize
  const leafData = new Float32Array(totalPixels * 4)
  const leafColors = new Float32Array(leafCount * 3)

  // Pre-parse tree hex colors to [r, g, b] floats
  const parsedColors = treeColors.map((hex) => {
    const c = new THREE.Color(hex || '#ffffff')
    return [c.r, c.g, c.b]
  })

  const baseClusterPerTree = Math.floor(clusterCount / treePositions.length)
  const treesWithExtraCluster = clusterCount % treePositions.length
  const leavesPerTree = Math.ceil(leafCount / treePositions.length)

  const boundsY = boundingBox.y
  const boundsX = boundingBox.x
  const offsetX = INITIAL_OFFSET.x
  const offsetY = INITIAL_OFFSET.y
  const offsetZ = INITIAL_OFFSET.z
  const PI2 = Math.PI * 2

  let leafIndex = 0
  for (let i = 0; i < treePositions.length; i++) {
    const treePos = treePositions[i]
    const treeRandom = mulberry32(randomSeed + i)
    const thisTreeClusterCount = baseClusterPerTree + (i < treesWithExtraCluster ? 1 : 0)

    // Generate cluster centers using spherical distribution within the bounding ellipsoid
    const clusterCenters = []
    for (let j = 0; j < thisTreeClusterCount; j++) {
      // Golden angle spread + random jitter for even coverage
      const angle = (j * 2.37) + treeRandom() * PI2
      // Even vertical spread from 40% to 100% of bounds height
      const t = (j + treeRandom() * 0.5) / thisTreeClusterCount
      const height = boundsY * (0.4 + 0.6 * t)
      // Ellipsoid radius at this height
      const normalizedH = height / boundsY
      const radius = boundsX * 0.5 * Math.sin(Math.PI * normalizedH)

      const cx = Math.cos(angle) * radius * (0.7 + treeRandom() * 0.3)
      const cy = height
      const cz = Math.sin(angle) * radius * (0.7 + treeRandom() * 0.3)
      clusterCenters.push({ x: cx, y: cy, z: cz })
    }

    const [tr, tg, tb] = parsedColors[i] || [1, 1, 1]

    for (let j = 0; j < leavesPerTree && leafIndex < leafCount; j++) {
      const clusterIndex = j % thisTreeClusterCount
      const cc = clusterCenters[clusterIndex]
      const leafRandom = mulberry32(leafIndex)

      // Uniform random point in sphere
      const theta = PI2 * leafRandom()
      const phi = Math.acos(2 * leafRandom() - 1)
      const r = clusterRadius * Math.pow(leafRandom(), 1 / 3)

      const sinPhi = Math.sin(phi)
      const index = leafIndex * 4
      leafData[index] = r * sinPhi * Math.cos(theta) + cc.x + treePos[0] + offsetX
      leafData[index + 1] = r * sinPhi * Math.sin(theta) + cc.y + treePos[1] + offsetY
      leafData[index + 2] = r * Math.cos(phi) + cc.z + treePos[2] + offsetZ
      leafData[index + 3] = 0.8 + leafRandom() * 0.4

      const cIndex = leafIndex * 3
      leafColors[cIndex] = tr
      leafColors[cIndex + 1] = tg
      leafColors[cIndex + 2] = tb

      leafIndex++
    }
  }

  for (let i = leafIndex * 4; i < totalPixels * 4; i += 4) {
    leafData[i] = INITIAL_OFFSET.x
    leafData[i + 1] = INITIAL_OFFSET.y
    leafData[i + 2] = INITIAL_OFFSET.z
    leafData[i + 3] = 1.0
  }

  return { leafData, leafColors, textureSize }
}

// ---- TSL rotation matrix around arbitrary axis ----
const rotateAroundAxis = Fn(([pos_immutable, axis_immutable, angle]) => {
  const pos = vec3(pos_immutable).toVar()
  const ax = vec3(axis_immutable).toVar()
  const c = cos(angle)
  const s = sin(angle)
  const oc = float(1.0).sub(c)

  const rx = pos.x.mul(oc.mul(ax.x).mul(ax.x).add(c))
    .add(pos.y.mul(oc.mul(ax.x).mul(ax.y).sub(ax.z.mul(s))))
    .add(pos.z.mul(oc.mul(ax.z).mul(ax.x).add(ax.y.mul(s))))

  const ry = pos.x.mul(oc.mul(ax.x).mul(ax.y).add(ax.z.mul(s)))
    .add(pos.y.mul(oc.mul(ax.y).mul(ax.y).add(c)))
    .add(pos.z.mul(oc.mul(ax.y).mul(ax.z).sub(ax.x.mul(s))))

  const rz = pos.x.mul(oc.mul(ax.z).mul(ax.x).sub(ax.y.mul(s)))
    .add(pos.y.mul(oc.mul(ax.y).mul(ax.z).add(ax.x.mul(s))))
    .add(pos.z.mul(oc.mul(ax.z).mul(ax.z).add(c)))

  return vec3(rx, ry, rz)
})

export function TreeLeavesSystem({ treePositions, treeColors = [], randomSeed = 12345 }) {
  const leavesRef = useRef()
  const trunksRef = useRef()
  const { nodes: trunkNodes } = useGLTF('/models/treeTrunks.glb')
  // KTX2, not the JPGs beside them (the masters): those sat on the GPU as
  // 22 MB of raw RGBA and mipmaps between them. Encoded --linear --flip
  // (scripts/ktx2-textures.mjs) because useTexture handed the shaders the
  // JPGs flipped and with no colour space, and the look is that.
  const leafAtlas = useKTX2Texture('/images/leaves_atlas.ktx2')
  const trunkTexture = useKTX2Texture('/images/st_birch_trunk_bc.ktx2')

  const trunkUvScaleUniform = useRef(uniform(new THREE.Vector2(TRUNK_UV.scaleU, TRUNK_UV.scaleV)))
  const trunkUvOffsetUniform = useRef(uniform(new THREE.Vector2(TRUNK_UV.offsetU, TRUNK_UV.offsetV)))

  const boundingBox = useMemo(
    () => new THREE.Vector3(LEAF_CONFIG.boundsX, LEAF_CONFIG.boundsY, LEAF_CONFIG.boundsZ),
    []
  )

  const meshData = useMemo(() => {
    const { clusterRadius } = LEAF_CONFIG
    const leafCount = Math.round(LEAF_CONFIG.leavesPerTree * treePositions.length)
    const clusterCount = Math.round(LEAF_CONFIG.clustersPerTree * treePositions.length)

    // ====== LEAVES ======
    const { leafData, leafColors, textureSize } = generateClusteredLeafPositions(
      treePositions, treeColors, leafCount, clusterCount, clusterRadius, boundingBox, randomSeed
    )

    const dataTexture = new THREE.DataTexture(leafData, textureSize, textureSize, THREE.RGBAFormat, THREE.FloatType)
    dataTexture.needsUpdate = true

    // 2x2 is enough segments for the wind bend to read; 6x6 was ~1.08M tris
    // across the pool, paid again in every shadow pass. ~9x fewer leaf verts.
    const leafGeometry = new THREE.PlaneGeometry(1, 1, 2, 2)
    const references = new Float32Array(leafCount * 2)
    for (let i = 0; i < leafCount; i++) {
      references[i * 2] = (i % textureSize) / textureSize
      references[i * 2 + 1] = Math.floor(i / textureSize) / textureSize
    }
    leafGeometry.setAttribute('reference', new THREE.InstancedBufferAttribute(references, 2))

    // Per-instance color from sceneConfig tree colors
    leafGeometry.setAttribute('leafColor', new THREE.InstancedBufferAttribute(leafColors, 3))

    // Per-instance atlas tile index (0–8 for 3x3 grid), seeded random
    const tileIndices = new Float32Array(leafCount)
    const tileRandom = mulberry32(randomSeed + 55555)
    for (let i = 0; i < leafCount; i++) {
      tileIndices[i] = Math.floor(tileRandom() * 9)
    }
    leafGeometry.setAttribute('tileIndex', new THREE.InstancedBufferAttribute(tileIndices, 1))

    // Unlit — keeps the flat anime pink by day (Lambert lit them but killed the
    // look). The colour is driven by mood.nightness so the leaves darken + take
    // a cool moonlit cast at night, and day is untouched (× white at nightness
    // 0). Perfect sync: nightness is the SAME shared uniform the sky + moon read.
    // LEAF_NIGHT_TINT is the per-channel multiplier at full night (dims R/G more
    // than B → shifts the pink toward moonlit blue-purple).
    const LEAF_NIGHT_TINT = vec3(0.0, 0.0, 0.05)
    const leafMaterial = new MeshBasicNodeMaterial()
    leafMaterial.side = THREE.DoubleSide
    // Alpha-TESTED, not alpha-blended: the atlas is a hard cutout, so opaque +
    // alphaTest keeps the identical look while writing depth and rejecting
    // overdraw (early-Z). transparent:true sent all 15k instances to the sorted
    // blend queue, which was the top overdraw cost in the scene.
    leafMaterial.transparent = false
    leafMaterial.alphaTest = 0.5
    leafMaterial.colorNode = attribute('leafColor', 'vec3')
      .mul(mix(vec3(1, 1, 1), LEAF_NIGHT_TINT, mood.nightness))

    const leafInitialOffset = uniform(INITIAL_OFFSET)
    const referenceAttr = attribute('reference', 'vec2')
    const tileIndexAttr = attribute('tileIndex', 'float')

    // Atlas-based alpha: remap UV into the correct 3x3 tile
    const ATLAS_COLS = float(3)
    const ATLAS_ROWS = float(3)
    leafMaterial.opacityNode = Fn(() => {
      const tileCol = mod(floor(tileIndexAttr), ATLAS_COLS)
      const tileRow = floor(tileIndexAttr.div(ATLAS_COLS))
      const atlasUv = vec2(
        uv().x.add(tileCol).div(ATLAS_COLS),
        uv().y.add(tileRow).div(ATLAS_ROWS)
      )
      return texture(leafAtlas, atlasUv).r
    })()

    leafMaterial.positionNode = Fn(() => {
      const leafData = texture(dataTexture, referenceAttr)
      const leafPosition = leafData.xyz.sub(leafInitialOffset).toVar()
      const leafScale = leafData.w

      // Wind — base oscillation per-leaf
      const windTime = time
      const windOffset = sin(windTime.add(leafPosition.x.mul(20.0)))
        .mul(cos(windTime.mul(0.7).add(leafPosition.z.mul(20.0))))
      const heightFactor = leafPosition.y.mul(0.1)
      const windMultiplied = windOffset.mul(float(1.0).add(heightFactor))

      const wind = TREE_WIND
      leafPosition.addAssign(vec3(
        sin(windTime).mul(wind.swayX),
        cos(windTime.mul(0.5)).mul(wind.swayY),
        sin(windTime.mul(0.7)).mul(wind.swayZ)
      ).mul(windMultiplied))

      // Rotation and tilt
      const rotation = fract(leafScale.mul(10.0)).mul(6.28318530718)
      const tilt = fract(leafScale.mul(100.0)).sub(0.5).mul(0.5)

      let transformed = positionLocal.toVar()

      // Per-vertex bending
      const bendFactor = positionLocal.y.add(0.5)
      const bendStrength = float(wind.bendStrength)

      const flapAngle = windOffset.mul(bendStrength).mul(bendFactor.mul(bendFactor))
      transformed.assign(rotateAroundAxis(transformed, vec3(1, 0, 0), flapAngle))

      const ripple = sin(windTime.mul(3.0).add(positionLocal.x.mul(6.0)).add(leafScale.mul(50.0)))
        .mul(wind.rippleStrength).mul(bendFactor)
      transformed.z.addAssign(ripple)

      const windRotation = windOffset.mul(wind.wholeSway)
      transformed.assign(rotateAroundAxis(transformed, vec3(1, 0, 0), windRotation))

      transformed.assign(rotateAroundAxis(transformed, vec3(0, 1, 0), rotation))

      const tiltAxis = vec3(cos(rotation), 0, sin(rotation).negate())
      transformed.assign(rotateAroundAxis(transformed, tiltAxis, tilt))

      return leafPosition.add(transformed)
    })()

    const instancedLeaves = new THREE.InstancedMesh(leafGeometry, leafMaterial, leafCount)
    // Leaves don't cast: 15k alpha-tested cards cast in every shadow pass read
    // as speckle on flat anime foliage anyway, and it was a huge share of the
    // shadow cost. Trunks + buildings still cast, so trees still ground.
    instancedLeaves.castShadow = false
    instancedLeaves.receiveShadow = false
    // Disable frustum culling: leaf positions are reconstructed in the shader
    // (offset by INITIAL_OFFSET), so Three.js cannot compute correct bounds.
    instancedLeaves.frustumCulled = false

    // ====== TRUNKS (3 variants from treeTrunks.glb) ======
    const trunkNames = ['st_birch_trunk', 'st_birch_trunk001', 'st_birch_trunk003']
    const trunkGeometries = trunkNames.map(name => trunkNodes[name]?.geometry).filter(Boolean)
    if (trunkGeometries.length === 0) {
      console.warn('[Trees] No trunk geometries found. Available nodes:', Object.keys(trunkNodes))
    }

    const trunkRandom = mulberry32(randomSeed + 77777)
    const variantCount = trunkGeometries.length || 1

    const positionsByVariant = Array.from({ length: variantCount }, () => [])
    treePositions.forEach((position) => {
      const variant = Math.floor(trunkRandom() * variantCount)
      positionsByVariant[variant].push(position)
    })

    const instancedTrunkMeshes = trunkGeometries.map((geo, vi) => {
      const mat = new MeshStandardNodeMaterial()
      const remappedUv = uv().mul(vec2(trunkUvScaleUniform.current)).add(vec2(trunkUvOffsetUniform.current))
      mat.colorNode = texture(trunkTexture, remappedUv)
      mat.roughness = 0.9
      mat.metalness = 0
      const positions = positionsByVariant[vi]
      const mesh = new THREE.InstancedMesh(geo, mat, positions.length)
      mesh.castShadow = true
      mesh.receiveShadow = true
      const matrix = new THREE.Matrix4()
      positions.forEach((pos, idx) => {
        matrix.setPosition(pos[0], pos[1], pos[2])
        mesh.setMatrixAt(idx, matrix)
      })
      mesh.instanceMatrix.needsUpdate = true
      return mesh
    })

    return { instancedLeaves, instancedTrunkMeshes, dataTexture }
  }, [treePositions, treeColors, randomSeed, trunkNodes, leafAtlas, trunkTexture, boundingBox])

  const { instancedLeaves, instancedTrunkMeshes, dataTexture: leafDataTex } = meshData

  useEffect(() => {
    const leavesGroup = leavesRef.current
    const trunksGroup = trunksRef.current

    if (instancedLeaves && leavesGroup) leavesGroup.add(instancedLeaves)
    if (instancedTrunkMeshes && trunksGroup) {
      instancedTrunkMeshes.forEach(m => trunksGroup.add(m))
    }

    return () => {
      if (instancedLeaves && leavesGroup) {
        leavesGroup.remove(instancedLeaves)
        instancedLeaves.geometry.dispose()
        instancedLeaves.material.dispose()
      }
      if (instancedTrunkMeshes && trunksGroup) {
        instancedTrunkMeshes.forEach(m => {
          trunksGroup.remove(m)
          m.material.dispose()
        })
      }
      if (leafDataTex) leafDataTex.dispose()
    }
  }, [instancedLeaves, instancedTrunkMeshes, leafDataTex])

  return (
    <group>
      <group ref={leavesRef} />
      <group ref={trunksRef} />
    </group>
  )
}

useGLTF.preload('/models/treeTrunks.glb')

/**
 * Default export: reads tree positions and colors from sceneConfig.
 * Drop-in replacement for the previous TreesTSL component.
 */
export default function TreesTSL() {
  const treePositions = useMemo(() => TREES.map(t => t.position), [])
  const treeColors = useMemo(() => TREES.map(t => t.color), [])
  return <TreeLeavesSystem treePositions={treePositions} treeColors={treeColors} />
}
